import { Controller } from '@hotwired/stimulus';
import { getCableConsumer } from '../channels/consumer';

export default class extends Controller {
  static values = {
    unreadCount: { type: Number, default: 0 },
    realtimeMode: { type: String, default: 'cable' },
    pollUrl: String,
    presenceUrl: String,
    pollInterval: { type: Number, default: 10000 },
    heartbeatInterval: { type: Number, default: 30000 }
  };

  connect() {
    this.unread = this.hasUnreadCountValue ? Math.max(0, this.unreadCountValue) : 0;
    this.updateBadges();
    this.pollTimer = null;
    this.heartbeatTimer = null;
    this.pollingActive = false;
    this.boundVisibilityChange = this.onVisibilityChange.bind(this);
    this.handleTurboLoad = this.handleTurboLoad.bind(this);
    document.addEventListener('turbo:load', this.handleTurboLoad);
    document.addEventListener('visibilitychange', this.boundVisibilityChange);

    if (this.realtimeModeValue === 'poll') {
      this.startPolling();
      this.startPresenceHeartbeat();
    } else {
      this.startCable();
      this.startInboxPolling();
    }
  }

  disconnect() {
    document.removeEventListener('visibilitychange', this.boundVisibilityChange);
    document.removeEventListener('turbo:load', this.handleTurboLoad);
    this.subscription?.unsubscribe();
    this.stopPolling();
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
  }

  handleTurboLoad() {
    this.refreshUnreadCount();
  }

  startCable() {
    this.subscription = getCableConsumer().subscriptions.create(
      { channel: 'Wizard::AdminChannel' },
      {
        received: (data) => this.onCableMessage(data),
        connected: () => this.startHeartbeat()
      }
    );
  }

  startInboxPolling() {
    this.pollingActive = true;
    this.clearPollTimer();
    this.pollInbox();
    this.schedulePoll();
  }

  startPolling() {
    this.pollingActive = true;
    this.clearPollTimer();
    this.touchPresence();
    this.pollInbox();
    this.schedulePoll();
  }

  stopPolling() {
    this.pollingActive = false;
    this.clearPollTimer();
  }

  clearPollTimer() {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  schedulePoll() {
    if (document.hidden) return;

    this.pollTimer = setTimeout(() => this.pollInbox(), this.pollIntervalValue);
  }

  onVisibilityChange() {
    if (document.hidden) {
      this.clearPollTimer();
      if (this.heartbeatTimer) {
        clearInterval(this.heartbeatTimer);
        this.heartbeatTimer = null;
      }
      return;
    }

    if (this.pollingActive) {
      this.pollInbox();
    }

    if (this.realtimeModeValue === 'poll' && !this.heartbeatTimer) {
      this.startPresenceHeartbeat();
    }
  }

  async pollInbox() {
    try {
      const response = await fetch(this.pollUrlValue, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });

      if (!response.ok) return;

      const data = await response.json();
      this.applyUnreadCount(data.unread_count);
    } catch {
      // ignore
    } finally {
      if (this.pollingActive) this.schedulePoll();
    }
  }

  async refreshUnreadCount() {
    try {
      const response = await fetch(this.pollUrlValue, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });

      if (!response.ok) return;

      const data = await response.json();
      this.applyUnreadCount(data.unread_count);
    } catch {
      // ignore
    }
  }

  startPresenceHeartbeat() {
    this.touchPresence();
    this.heartbeatTimer = setInterval(() => this.touchPresence(), this.heartbeatIntervalValue);
  }

  async touchPresence() {
    try {
      await fetch(this.presenceUrlValue, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': this.csrfToken()
        }
      });
    } catch {
      // ignore
    }
  }

  startHeartbeat() {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = setInterval(() => {
      this.subscription?.send({ type: 'heartbeat' });
    }, this.heartbeatIntervalValue);
  }

  onCableMessage(data) {
    if (data.type === 'inbox' && typeof data.unread_count === 'number') {
      this.applyUnreadCount(data.unread_count);
      return;
    }

    if (data.type !== 'message' || data.message?.sender_role !== 'user') return;
    this.refreshUnreadCount();
  }

  applyUnreadCount(count) {
    if (typeof count !== 'number') return;
    this.unread = Math.max(0, count);
    this.updateBadges();
  }

  updateBadges() {
    const hasUnread = this.unread > 0;
    const label = hasUnread ? (this.unread > 9 ? '9+' : String(this.unread)) : '';

    document.querySelectorAll('[data-wizard-wand-trigger]').forEach((trigger) => {
      trigger.classList.toggle('wizard-wand--unread', hasUnread);
      const unreadNote = hasUnread
        ? ` (${this.unread} unread ${this.unread === 1 ? 'message' : 'messages'})`
        : '';
      const baseLabel = trigger.tagName === 'A' ? 'Wizard inbox' : 'Get Sheets Help from the Wizard';
      trigger.setAttribute('aria-label', `${baseLabel}${unreadNote}`);
    });

    document.querySelectorAll('[data-wizard-wand-badge]').forEach((badge) => {
      if (hasUnread) {
        badge.textContent = label;
        badge.classList.add('wizard-wand__badge--notify');
        badge.classList.remove('hidden');
        badge.setAttribute('aria-hidden', 'false');
      } else {
        badge.textContent = '';
        badge.classList.remove('wizard-wand__badge--notify');
        badge.classList.add('hidden');
        badge.setAttribute('aria-hidden', 'true');
      }
    });
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
  }
}
